import {
  Effect,
  Domain,
  createDomain,
  guard,
  Unit,
  sample,
  forward
} from 'effector';

export type RequestStatusType = 'done' | 'fail' | 'loading';

export const createRequestStatus = (
  config: {
    effect: Effect<any, any, any>;
    initialValue?: RequestStatusType;
    once?: boolean;
  },
  params?: {
    domain?: Domain;
    reset?: Unit<any>;
  }
) => {
  const { effect, initialValue = 'loading', once = false } = config;
  const { domain = createDomain() } = params || {};
  const { createEvent, createStore } = domain;
  const reset = params?.reset || createEvent();
  const setValue = createEvent<RequestStatusType>();

  const $loading = createStore<RequestStatusType>(initialValue);
  $loading.on(setValue, (_, v) => v).reset(reset);

  const onceGuard = guard(sample($loading, effect), {
    filter: loading => {
      if (once && loading !== 'loading') return false;
      return true;
    }
  });

  sample({ source: effect.done, target: setValue.prepend(() => 'done') });
  sample({
    source: effect.fail,
    target: setValue.prepend(x => {
      if (x?.error?.indexOf?.('AbortError') > -1) {
        return 'done';
      }
      return 'fail';
    })
  });
  sample({ source: onceGuard, target: setValue.prepend(() => 'loading') });

  return $loading;
};

export const createMultiRequestStatus = (
  config: {
    effects: Effect<any, any, any>[];
    initialValue?: RequestStatusType;
  },
  params?: {
    domain?: Domain;
    reset?: Unit<any>;
  }
) => {
  const { effects, initialValue = 'loading' } = config;
  const { domain = createDomain() } = params || {};
  const { createEvent, createStore } = domain;
  const reset = params?.reset || createEvent();
  const setValue = createEvent<RequestStatusType>();

  const $loading = createStore<RequestStatusType>(initialValue);
  $loading.on(setValue, (_, v) => v).reset(reset);

  const changeStatus = createEvent<{
    status: RequestStatusType;
    index: number;
  }>();

  const $statuses = createStore(
    Array.from({ length: effects.length }).fill(
      'loading'
    ) as RequestStatusType[]
  ).reset(reset);

  const $result = $statuses.map<RequestStatusType>(x => {
    if (x.filter(x => x === 'done').length === effects.length) return 'done';
    if (x.filter(x => x === 'fail').length > 0) return 'fail';
    return 'loading';
  });

  forward({ from: $result, to: setValue });

  effects.forEach((effect, index) => {
    forward({
      from: createRequestStatus({ effect }).updates,
      to: changeStatus.prepend(x => ({ status: x, index }))
    });
  });

  for (const effect of effects) {
    createRequestStatus({ effect }).updates;
  }

  return $loading;
};
